import { useState, useEffect, useCallback } from "react";
import { Layout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Users,
  UserPlus,
  Edit,
  Trash2,
  Shield,
  Eye,
  RefreshCw,
  UserCheck,
  UserX,
  Clock,
  CheckCircle,
  XCircle,
  Mail,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/utils";

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [unverifiedUsers, setUnverifiedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Form states
  const [formEmail, setFormEmail] = useState("");
  const [formPassword, setFormPassword] = useState("");
  const [formRole, setFormRole] = useState("viewer");
  const [submitting, setSubmitting] = useState(false);

  const fetchUsers = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const [usersRes, pendingRes, unverifiedRes] = await Promise.all([
        api.get(`/api/users`),
        api.get(`/api/users/pending`),
        api.get(`/api/users/unverified`)
      ]);
      setUsers(usersRes.data);
      setPendingUsers(pendingRes.data);
      setUnverifiedUsers(unverifiedRes.data);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const resetForm = () => {
    setFormEmail("");
    setFormPassword("");
    setFormRole("viewer");
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await api.post(`/api/auth/register`, {
        email: formEmail,
        password: formPassword,
        role: formRole,
      });
      toast.success("User created successfully");
      setAddDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to create user";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    setSubmitting(true);

    try {
      const updateData = { role: formRole };
      if (formPassword) {
        updateData.password = formPassword;
      }

      await api.put(`/api/users/${selectedUser.id}`, updateData);
      toast.success("User updated successfully");
      setEditDialogOpen(false);
      resetForm();
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to update user";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    setSubmitting(true);

    try {
      await api.delete(`/api/users/${selectedUser.id}`);
      toast.success("User deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to delete user";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveUser = async (user) => {
    try {
      await api.post(`/api/users/${user.id}/approve`);
      toast.success(`${user.email} has been approved`);
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to approve user";
      toast.error(message);
    }
  };

  const handleRejectUser = async (user) => {
    try {
      await api.post(`/api/users/${user.id}/reject`);
      toast.success(`${user.email} has been rejected`);
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to reject user";
      toast.error(message);
    }
  };

  const handleVerifyManually = async (user) => {
    try {
      await api.post(`/api/users/${user.id}/verify-manual`);
      toast.success(`${user.email} has been manually verified`);
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to verify user";
      toast.error(message);
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setFormEmail(user.email);
    setFormRole(user.role);
    setFormPassword("");
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const formatDate = (isoString) => {
    return new Date(isoString).toLocaleDateString("en-MY", {
      year: "numeric",
      month: "short",
      day: "numeric",
      timeZone: "Asia/Kuala_Lumpur"
    });
  };

  const approvedUsers = users.filter(u => u.is_approved);

  return (
    <Layout>
      <div className="container mx-auto p-6 md:p-8" data-testid="users-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight">
              USER MANAGEMENT
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage system users and approve new registrations
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchUsers(true)}
              disabled={refreshing}
              data-testid="refresh-users-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              onClick={() => {
                resetForm();
                setAddDialogOpen(true);
              }}
              data-testid="add-user-btn"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Add User
            </Button>
          </div>
        </div>

        <Tabs defaultValue={unverifiedUsers.length > 0 ? "unverified" : (pendingUsers.length > 0 ? "pending" : "approved")} className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="unverified" className="gap-2" data-testid="tab-unverified">
              <Mail className="h-4 w-4" />
              Unverified
              {unverifiedUsers.length > 0 && (
                <Badge variant="outline" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs border-yellow-500 text-yellow-500">
                  {unverifiedUsers.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="pending" className="gap-2" data-testid="tab-pending">
              <Clock className="h-4 w-4" />
              Pending Approval
              {pendingUsers.length > 0 && (
                <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                  {pendingUsers.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="approved" className="gap-2" data-testid="tab-approved">
              <UserCheck className="h-4 w-4" />
              Approved Users ({approvedUsers.length})
            </TabsTrigger>
          </TabsList>

          {/* Unverified Users Tab */}
          <TabsContent value="unverified">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(3)].map((_, i) => (
                  <Card key={i} className="bg-card/50 border-border">
                    <CardContent className="p-6">
                      <div className="space-y-4">
                        <div className="h-4 w-3/4 bg-muted rounded animate-shimmer" />
                        <div className="h-4 w-1/2 bg-muted rounded animate-shimmer" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : unverifiedUsers.length === 0 ? (
              <Card className="bg-card/50 border-border">
                <CardContent className="py-12 text-center">
                  <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500 opacity-50" />
                  <p className="text-muted-foreground">No unverified users</p>
                  <p className="text-sm text-muted-foreground mt-1">All users have verified their email</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                  <p className="text-sm text-yellow-500">
                    <strong>Note:</strong> These users signed up but haven't clicked the verification link in their email. 
                    You can manually verify them if the email was not received.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {unverifiedUsers.map((user) => (
                    <Card
                      key={user.id}
                      className="bg-card/50 border-orange-500/30 hover:border-orange-500/50 transition-colors"
                      data-testid={`unverified-user-${user.id}`}
                    >
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-orange-500/10 flex items-center justify-center">
                              <Mail className="h-5 w-5 text-orange-500" />
                            </div>
                            <div>
                              <CardTitle className="text-base truncate max-w-[180px]">
                                {user.email}
                              </CardTitle>
                              <Badge variant="outline" className="mt-1 text-orange-500 border-orange-500/30">
                                Email Not Verified
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-xs text-muted-foreground mb-4">
                          Registered {formatDate(user.created_at)}
                        </p>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="flex-1"
                            onClick={() => handleVerifyManually(user)}
                            data-testid={`verify-user-${user.id}`}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Verify Manually
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-destructive hover:text-destructive border-destructive/30 hover:border-destructive/50"
                            onClick={() => handleRejectUser(user)}
                            data-testid={`reject-unverified-${user.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Pending Users Tab */}
          <TabsContent value="pending">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(3)].map((_, i) => (
                  <Card key={i} className="bg-card/50 border-border">
                    <CardContent className="p-6">
                      <div className="space-y-4">
                        <div className="h-4 w-3/4 bg-muted rounded animate-shimmer" />
                        <div className="h-4 w-1/2 bg-muted rounded animate-shimmer" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : pendingUsers.length === 0 ? (
              <Card className="bg-card/50 border-border">
                <CardContent className="py-12 text-center">
                  <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500 opacity-50" />
                  <p className="text-muted-foreground">No pending approvals</p>
                  <p className="text-sm text-muted-foreground mt-1">All user registrations have been processed</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {pendingUsers.map((user) => (
                  <Card
                    key={user.id}
                    className="bg-card/50 border-yellow-500/30 hover:border-yellow-500/50 transition-colors"
                    data-testid={`pending-user-${user.id}`}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-yellow-500/10 flex items-center justify-center">
                            <Clock className="h-5 w-5 text-yellow-500" />
                          </div>
                          <div>
                            <CardTitle className="text-base truncate max-w-[180px]">
                              {user.email}
                            </CardTitle>
                            <Badge variant="outline" className="mt-1 text-yellow-500 border-yellow-500/30">
                              Pending Approval
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xs text-muted-foreground mb-4">
                        Registered {formatDate(user.created_at)}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="flex-1 bg-green-600 hover:bg-green-700"
                          onClick={() => handleApproveUser(user)}
                          data-testid={`approve-user-${user.id}`}
                        >
                          <UserCheck className="h-4 w-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-destructive hover:text-destructive border-destructive/30 hover:border-destructive/50"
                          onClick={() => handleRejectUser(user)}
                          data-testid={`reject-user-${user.id}`}
                        >
                          <UserX className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Approved Users Tab */}
          <TabsContent value="approved">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <Card key={i} className="bg-card/50 border-border">
                    <CardContent className="p-6">
                      <div className="space-y-4">
                        <div className="h-4 w-3/4 bg-muted rounded animate-shimmer" />
                        <div className="h-4 w-1/2 bg-muted rounded animate-shimmer" />
                        <div className="h-4 w-1/3 bg-muted rounded animate-shimmer" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : approvedUsers.length === 0 ? (
              <Card className="bg-card/50 border-border">
                <CardContent className="py-12 text-center">
                  <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">No approved users</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {approvedUsers.map((user) => (
                  <Card
                    key={user.id}
                    className="bg-card/50 border-border hover:border-primary/50 transition-colors"
                    data-testid={`user-card-${user.id}`}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                            {user.role === "admin" ? (
                              <Shield className="h-5 w-5 text-primary" />
                            ) : (
                              <Eye className="h-5 w-5 text-muted-foreground" />
                            )}
                          </div>
                          <div>
                            <CardTitle className="text-base truncate max-w-[180px]">
                              {user.email}
                            </CardTitle>
                            <Badge
                              variant={user.role === "admin" ? "default" : "secondary"}
                              className="mt-1 capitalize"
                            >
                              {user.role}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xs text-muted-foreground mb-4">
                        Joined {formatDate(user.created_at)}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => openEditDialog(user)}
                          data-testid={`edit-user-${user.id}`}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => openDeleteDialog(user)}
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Add User Dialog */}
        <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="h-5 w-5" />
                Add New User
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddUser} className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Users added by admin are automatically verified and approved.
              </p>
              <div className="space-y-2">
                <Label htmlFor="add-email">Email</Label>
                <Input
                  id="add-email"
                  type="email"
                  value={formEmail}
                  onChange={(e) => setFormEmail(e.target.value)}
                  placeholder="user@example.com"
                  required
                  data-testid="add-user-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="add-password">Password</Label>
                <Input
                  id="add-password"
                  type="password"
                  value={formPassword}
                  onChange={(e) => setFormPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  data-testid="add-user-password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="add-role">Role</Label>
                <Select value={formRole} onValueChange={setFormRole}>
                  <SelectTrigger data-testid="add-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="viewer">Viewer</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setAddDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} data-testid="submit-add-user">
                  {submitting ? "Creating..." : "Create User"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="h-5 w-5" />
                Edit User
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEditUser} className="space-y-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input value={formEmail} disabled className="bg-muted" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-password">New Password (optional)</Label>
                <Input
                  id="edit-password"
                  type="password"
                  value={formPassword}
                  onChange={(e) => setFormPassword(e.target.value)}
                  placeholder="Leave blank to keep current"
                  data-testid="edit-user-password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-role">Role</Label>
                <Select value={formRole} onValueChange={setFormRole}>
                  <SelectTrigger data-testid="edit-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="viewer">Viewer</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} data-testid="submit-edit-user">
                  {submitting ? "Saving..." : "Save Changes"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete User</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete{" "}
                <span className="font-medium text-foreground">{selectedUser?.email}</span>?
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteUser}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                data-testid="confirm-delete-user"
              >
                {submitting ? "Deleting..." : "Delete"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
